import 'dayjs/locale/nl';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'dayjs/locale/de';

import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(LocalizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);

export type DateLike = dayjs.ConfigType;
export type TimeObject = {
  hour: number;
  minute: number;
};

export function setDateLocale(locale: string = 'nl') {
  dayjs.locale(locale);
}

export function formatDate(date: DateLike, format: string = 'DD/MM/YYYY') {
  return dayjs(date).format(format);
}

export function formatDateTime(date: DateLike): string {
  return formatDate(date, 'ddd L LT');
}

export function formatTime(date: DateLike, interval: number = 1) {
  let formatted = dayjs(date).format('HH:mm');
  if (interval > 1) {
    let parsed = parseTime(formatted);
    return formatTimeFromTimeObject({
      hour: parsed.hour,
      minute: Math.ceil(parsed.minute / interval) * interval,
    });
  }
  return formatted;
}

export function formatDateForInput(date: DateLike) {
  return formatDate(date, 'YYYY-MM-DD');
}

export function timeDifference(date: DateLike, reference: DateLike): string {
  return dayjs(date).from(reference);
}

export function parseTime(time: string): TimeObject {
  let timeSplit = time.split(':').map((i) => parseInt(i, 10));

  if (timeSplit.length !== 2) {
    return {
      hour: timeSplit[0] ? timeSplit[0] : 0,
      minute: timeSplit[1] ? timeSplit[1] : 0,
    };
  }

  return {
    hour: timeSplit[0],
    minute: timeSplit[1],
  };
}

export function formatTimeFromTimeObject(time: TimeObject): string {
  return `${time.hour.toString(10).padStart(2, '0')}:${time.minute.toString(10).padStart(2, '0')}`;
}

export function datetimeToUTC(date: DateLike, time: string) {
  let parsedTime = parseTime(time);
  let dateTime = dayjs(date);
  dateTime = dateTime.set('hour', parsedTime.hour).set('minute', parsedTime.minute);
  return dateTime.utc().format();
}

export function millisDifference(date: DateLike, reference: DateLike): number {
  return dayjs(date).diff(reference, 'ms');
}

export type CompactDurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export function getCompactDuration(
  date: DateLike,
  reference: DateLike = Date.now()
): { value: number; unit: CompactDurationUnit } {
  const seconds = Math.max(0, dayjs(reference).diff(dayjs(date), 'second'));

  if (seconds <= 100) {
    return { value: seconds, unit: 'seconds' };
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return { value: minutes, unit: 'minutes' };
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return { value: hours, unit: 'hours' };
  }

  return { value: Math.floor(hours / 24), unit: 'days' };
}

export type RelativeValidUntilKind = 'today' | 'tomorrow' | 'dayAfterTomorrow' | 'date';

/** Relative valid-until label parts: today/tomorrow/day-after, else localized day+month (+ year if needed). */
export function getRelativeValidUntil(
  date: DateLike,
  reference: DateLike = Date.now()
): { kind: RelativeValidUntilKind; dateLabel?: string } {
  const target = dayjs(date).startOf('day');
  const today = dayjs(reference).startOf('day');
  const diffDays = target.diff(today, 'day');

  if (diffDays === 0) {
    return { kind: 'today' };
  }
  if (diffDays === 1) {
    return { kind: 'tomorrow' };
  }
  if (diffDays === 2) {
    return { kind: 'dayAfterTomorrow' };
  }

  const dateLabel = target.year() === today.year() ? target.format('D MMMM') : target.format('D MMMM YYYY');

  return { kind: 'date', dateLabel };
}

const VALID_UNTIL_KEYS: Record<
  RelativeValidUntilKind,
  'validUntilToday' | 'validUntilTomorrow' | 'validUntilDayAfterTomorrow' | 'validUntilDate'
> = {
  today: 'validUntilToday',
  tomorrow: 'validUntilTomorrow',
  dayAfterTomorrow: 'validUntilDayAfterTomorrow',
  date: 'validUntilDate',
};

export function formatLocalizedValidUntil(
  date: DateLike,
  translate: (key: string, params?: Record<string, string | number>) => string,
  reference: DateLike = Date.now()
): string {
  const relative = getRelativeValidUntil(date, reference);
  const key = VALID_UNTIL_KEYS[relative.kind];

  if (relative.kind === 'date') {
    return translate(key, { date: relative.dateLabel || '' });
  }

  return translate(key);
}
